import { $ } from "../lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BG2, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { DATE_RANGES, MOCK_TODAY, filterJobsByDate, filterUntaggedByDate } from "../lib/dates";
import { HomeWelcome } from "../components/HomeWelcome";
import { IncompleteChip } from "../components/IncompleteChip";
import { JOB_SORT_CHOICES } from "../responsive";
import { KpiModal } from "../components/KpiModal";
import { RevenueGoals } from "../components/RevenueGoals";
import { compareMargin, jobCompleteness, marginNeedsMoreData, marginView } from "../jobCompleteness";
import { costSummaryLine } from "../businessPreset";
import { useBusinessType } from "../useBusinessType";
import { attentionRows, emptyFilterLine, FILTER_NAMES, periodLine } from "../homeText";
import { formatMoney } from "../money";
import { priorPeriodBounds, undatedForRange } from "../dateRange";
import { qbBannerMessage } from "../qbBanner";
import { useDevice, useIsMobile } from "../hooks/useDevice";
import { useEffect, useMemo, useRef, useState } from "react";

function Dashboard({ businessName, greetingName, onJobClick, onEstimate, onJumpToInbox, onClientClick, onCompare, jobSummaries, untagged, overhead, dismissed, qbConnected, dataSource, userId, onConnectQuickBooks, qbStarting, clientType, dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd, revenueGoals, onAddGoal, onEditGoal, onDeleteGoal, onMarkCompleteGoal, onArchiveGoal }) {
  const isMobile = useIsMobile();
  const device = useDevice();
  const { businessType, rule } = useBusinessType(); // how costs are labelled, and whether missing labor counts
  const compact = device !== "desktop"; // phones and tablets open on a Home screen with the welcome block
  const [jobFilter, setJobFilter] = useState(null); // phone and tablet only: "unbilled" | "loss" | "atrisk" | "incomplete" | "lowmargin" | null
  const jobsRef = useRef(null);
  const [sort, setSort]             = useState("profit");
  const [sortDir, setSortDir]       = useState("desc");
  const [expenseView, setExpenseView] = useState("job");
  const [activeKpi, setActiveKpi]     = useState(null); // 'revenue' | 'expenses' | 'profit' | 'jobs' | 'quality'
  const [alertsOpen, setAlertsOpen]   = useState(false);
  const [jobTableRows, setJobTableRows] = useState(10); // rows shown in job table
  const [searchQ, setSearchQ]           = useState("");
  const [searchOpen, setSearchOpen]     = useState(false);
  const searchRef                       = useRef(null);

  // Close search dropdown on outside click
  useEffect(() => {
    function handleClick(e) { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Apply date filter
  const filteredJobs     = dateRange === "all" ? jobSummaries : filterJobsByDate(jobSummaries, dateRange, customStart, customEnd);
  const filteredUntagged = filterUntaggedByDate(untagged, dateRange, customStart, customEnd);


  // Job type filter
  const typeFilteredJobs = filteredJobs;

  function handleColSort(col) {
    if (sort === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSort(col); setSortDir("desc"); }
  }

  // What each job is missing, and how its margin is shown (see src/jobCompleteness.js). The pending list
  // is every expense still waiting for a job, including the ones with a suggested job.
  const infoById = {};
  typeFilteredJobs.forEach(j => { const c = jobCompleteness(j, untagged, rule); infoById[j.id] = { c, m: marginView(j, c) }; });
  const infoOf = j => infoById[j.id] || (() => { const c = jobCompleteness(j, untagged, rule); return { c, m: marginView(j, c) }; })();
  // Margin note for the alert lists: the number if it is a real one, else say why not.
  const marginNote = j => {
    const m = infoOf(j).m;
    if (m.kind === "number") return m.text;
    if (m.kind === "incomplete") return `${m.value.toFixed(1)}% so far, incomplete`;
    return "no revenue";
  };

  // Lists behind the alerts and the "Needs your attention" rows (declared here so the job list can use them).
  const unbilledJobs = jobSummaries.filter(j => j.costs > 0 && j.revenue === 0);
  const unbilledIds = new Set(unbilledJobs.map(j => j.id));
  const incompleteJobs = typeFilteredJobs.filter(j => infoOf(j).c.status === "incomplete" && !unbilledIds.has(j.id));
  const lossJobs      = typeFilteredJobs.filter(j => j.status !== "In Progress" && j.profit < 0);
  const atRiskJobs = typeFilteredJobs.filter(j =>
    j.status === "In Progress" && j.revenue > 0 && (j.costs / j.revenue) > 0.85
  );
  const lowMarginJobs = typeFilteredJobs.filter(j => j.status !== "In Progress" && j.profit >= 0 && j.revenue > 0 && infoOf(j).m.value < 10);
  // Phones and tablets: tapping a row under "Needs your attention" narrows the job list to those jobs.
  const filterJobs = { unbilled: unbilledJobs, loss: lossJobs, atrisk: atRiskJobs, incomplete: incompleteJobs, lowmargin: lowMarginJobs }[jobFilter];
  const shownJobs = compact && filterJobs ? filterJobs : typeFilteredJobs;

  const sorted = [...shownJobs].sort((a,b) => {
    if (sort==="margin") return compareMargin(infoOf(a).m, infoOf(b).m, sortDir);
    let diff = 0;
    if (sort==="profit")        diff = b.profit - a.profit;
    else if (sort==="revenue")  diff = b.revenue - a.revenue;
    else if (sort==="costs")    diff = b.costs - a.costs;
    else if (sort==="material") diff = (b.materialCost||0) - (a.materialCost||0);
    else if (sort==="labor")    diff = (b.laborCost||0) - (a.laborCost||0);
    else if (sort==="name")     diff = a.name.localeCompare(b.name);
    else if (sort==="client")   diff = a.clientName.localeCompare(b.clientName);
    else if (sort==="status")   diff = a.status.localeCompare(b.status);
    return sortDir === "asc" ? -diff : diff;
  });

  const totalRev      = typeFilteredJobs.reduce((s,j) => s + j.revenue, 0);
  const totalCost     = typeFilteredJobs.reduce((s,j) => s + j.costs, 0);
  const totalMaterial = typeFilteredJobs.reduce((s,j) => s + (j.materialCost || 0), 0);
  const totalLabor    = typeFilteredJobs.reduce((s,j) => s + (j.laborCost || 0), 0);
  const totalProfit   = totalRev - totalCost;

  // Data Quality Score — all four components filtered to the selected date range.
  // Dismissed items stay in the denominator so dismissing doesn't artificially inflate the score.
  const filteredOverhead      = filterUntaggedByDate(overhead || [], dateRange, customStart, customEnd);
  const filteredDismissed     = filterUntaggedByDate(dismissed || [], dateRange, customStart, customEnd);
  const totalTaggedExpenses   = filteredJobs.reduce((s,j) => s + j.purchases.length, 0);
  const totalOverheadExpenses = filteredOverhead.length;
  const totalUntaggedExpenses = filteredUntagged.length;
  const totalDismissedExpenses = filteredDismissed.length;
  const totalExpenses  = totalTaggedExpenses + totalOverheadExpenses + totalUntaggedExpenses + totalDismissedExpenses;
  const accountedFor   = totalTaggedExpenses + totalOverheadExpenses;
  const dataQuality    = totalExpenses > 0 ? Math.round((accountedFor / totalExpenses) * 100) : 100;
  const dqColor        = dataQuality >= 80 ? ACCENT2 : dataQuality >= 50 ? AMBER : RED;

  const totalOverhead = filteredOverhead.reduce((s, o) => s + (o.amount || 0), 0);

  // Hero display values — profit, margin, color (depends on expenseView + totalOverhead)
  const heroIsNet  = expenseView === "fixed";
  const heroProfit = heroIsNet ? totalProfit - totalOverhead : totalProfit;
  const heroMargin = totalRev > 0 ? ((heroProfit / totalRev) * 100).toFixed(1) : null; // null = no revenue, no margin
  const heroColor  = heroProfit >= 0 ? ACCENT2 : RED;

  // ── Prior-period comparison — used for % change in KPI cards ──
  // Computes the equivalent previous window for each range key, then sums job data.
  const priorPeriodComparison = useMemo(() => {
    if (dateRange === "all" || dateRange === "custom") return null;
    const pb = priorPeriodBounds(dateRange, MOCK_TODAY);
    if (!pb) return null;
    const priorStart = pb.start, priorEnd = pb.end;
    const pJobs  = filterJobsByDate(jobSummaries, "custom", priorStart, priorEnd);
    const pRev   = pJobs.reduce((s, j) => s + j.revenue, 0);
    const pCost  = pJobs.reduce((s, j) => s + j.costs, 0);
    const pProfit = pRev - pCost;
    // Name the whole earlier period plainly ("vs all of September"). Built from the local year and month, never UTC.
    const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const [py, pmo] = pb.start.split("-").map(Number);
    // Add the year only when the earlier period is in a different year than today (January vs December, Q1 vs Q4).
    const yr = py !== MOCK_TODAY.getFullYear() ? ` ${py}` : "";
    const lbl = dateRange === "mtd" ? `vs all of ${MONTHS[pmo - 1]}${yr}`
      : dateRange === "qtd" ? `vs all of Q${Math.floor((pmo - 1) / 3) + 1}${yr}`
      : `vs all of ${py}`;
    return { pRev, pCost, pProfit, lbl };
  }, [jobSummaries, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const ppcRevPct    = priorPeriodComparison && priorPeriodComparison.pRev    !== 0 ? Math.round(((totalRev    - priorPeriodComparison.pRev)    / Math.abs(priorPeriodComparison.pRev))    * 100) : null;
  const ppcCostPct   = priorPeriodComparison && priorPeriodComparison.pCost   !== 0 ? Math.round(((totalCost   - priorPeriodComparison.pCost)   / Math.abs(priorPeriodComparison.pCost))   * 100) : null;
  const ppcProfitPct = priorPeriodComparison && priorPeriodComparison.pProfit !== 0 ? Math.round(((heroProfit  - priorPeriodComparison.pProfit) / Math.abs(priorPeriodComparison.pProfit)) * 100) : null;
  const ppcLbl = priorPeriodComparison?.lbl ?? "";

  // Mid-job margin alerts

  // Unbilled work alert

  // Jobs whose numbers are still missing something. A job is never called healthy while it is on this list:
  // its costs so far are a floor, so the real margin can only be lower than shown. Unbilled jobs have their own alert.
  // Jobs whose margin is missing costs or labor, so the totals above may look better than they are.
  const marginMissingCount = typeFilteredJobs.filter(j => { return marginNeedsMoreData(infoOf(j).c.flags); }).length;

  // Date range label — dynamically built from actual data dates
  const allDates = jobSummaries.flatMap(j => j.invoices.map(i => i.TxnDate)).filter(Boolean).sort();
  const firstDate = allDates[0] ? new Date(allDates[0]).toLocaleDateString('en-US', { month:'short', year:'numeric' }) : '';
  const lastDate  = allDates[allDates.length-1] ? new Date(allDates[allDates.length-1]).toLocaleDateString('en-US', { month:'short', year:'numeric' }) : '';
  const allTimeLabel = firstDate && lastDate && firstDate !== lastDate
    ? `All jobs · ${firstDate} – ${lastDate}`
    : firstDate ? `All jobs · ${firstDate}` : "All jobs";

  // Money with no date can't be placed in a date range, so it is left out. Say how much.
  const leftOut = dateRange === "all" ? { costCents: 0, revCents: 0 } : undatedForRange(jobSummaries, dateRange, customStart, customEnd, MOCK_TODAY);
  const leftOutParts = [leftOut.costCents !== 0 && `${formatMoney(leftOut.costCents / 100)} of costs`, leftOut.revCents !== 0 && `${formatMoney(leftOut.revCents / 100)} of revenue`].filter(Boolean);
  const leftOutNote = leftOutParts.length ? ` · Leaves out ${leftOutParts.join(" and ")} with no date` : "";
  const rangeLabel = (dateRange === "custom"
    ? `${customStart || "…"} → ${customEnd || "…"} · ${typeFilteredJobs.length} job${typeFilteredJobs.length!==1?"s":""}`
    : dateRange === "all"
      ? allTimeLabel
      : `${DATE_RANGES.find(r=>r.key===dateRange)?.label ?? dateRange.toUpperCase()} · ${typeFilteredJobs.length} job${typeFilteredJobs.length!==1?"s":""}`) + leftOutNote;

  // ── Additional alert types ──

  // ── Search — jobs and unique clients across all (unfiltered) summaries ──
  const uniqueClients = useMemo(() => {
    const seen = new Set();
    return jobSummaries.map(j => j.clientName).filter(n => n && !seen.has(n) && seen.add(n)).sort();
  }, [jobSummaries]);

  const searchResults = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return { jobs: [], clients: [] };
    return {
      jobs:    jobSummaries.filter(j => j.name.toLowerCase().includes(q)).slice(0, 6),
      clients: uniqueClients.filter(c => c.toLowerCase().includes(q)).slice(0, 4),
    };
  }, [searchQ, jobSummaries, uniqueClients]);

  const hasSearchResults = searchResults.jobs.length > 0 || searchResults.clients.length > 0;

  return (
    <div className="r-page" style={{ padding: isMobile ? "16px 16px 80px" : "32px 36px", background:BG, minHeight:"100vh" }}>

      {compact && (
        <HomeWelcome
          hour={new Date().getHours()} name={greetingName} period={periodLine(dateRange, customStart, customEnd)}
          profit={heroProfit} profitIsNet={heroIsNet} profitPct={ppcProfitPct} revenue={totalRev} revenuePct={ppcRevPct} comparisonLabel={ppcLbl}
          margin={heroMargin} marginNote={heroMargin === null ? "No revenue recorded yet" : marginMissingCount > 0 ? "So far. Some jobs are missing costs." : "Profit as a share of revenue"}
          expenseView={expenseView} onExpenseView={setExpenseView} onOpen={setActiveKpi}
          rows={attentionRows({ expenses: filteredUntagged.length, loss: lossJobs.length, atrisk: atRiskJobs.length, unbilled: unbilledJobs.length, incomplete: incompleteJobs.length, lowmargin: lowMarginJobs.length }, clientType === "quickbooks")}
          activeRow={jobFilter}
          onRow={key => {
            if (key === "expenses") { onJumpToInbox && onJumpToInbox(); return; }
            setJobFilter(f => (f === key ? null : key));
            setJobTableRows(10);
            setTimeout(() => { try { jobsRef.current && jobsRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* no scrolling here */ } }, 0);
          }}
        />
      )}

      {/* Page title + search + date slicer */}
      <div className="r-head" style={{ display:"flex",flexWrap:"wrap",alignItems:"flex-start",justifyContent:"space-between",marginBottom:28,gap:20 }}>
        <div style={{ flexShrink:0 }}>
          {!compact && <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>Job Profitability Overview</h1>}
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:compact?14:13,color:DIM,marginTop:compact?0:4 }}>{rangeLabel}</p>
        </div>

        {/* ── Search bar ── */}
        <div ref={searchRef} className="r-grow" style={{ position:"relative", flex:"0 0 260px" }}>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", fontSize:13, color:DIM, pointerEvents:"none" }}>⌕</span>
            <input
              type="text"
              placeholder="Search jobs or clients…"
              value={searchQ}
              onChange={e => { setSearchQ(e.target.value); setSearchOpen(true); }}
              onFocus={e => { e.currentTarget.style.borderColor = ACCENT; setSearchOpen(true); }}
              onBlur={e => { e.currentTarget.style.borderColor = BORDER; }}
              style={{ width:"100%", boxSizing:"border-box", padding:"8px 12px 8px 32px", borderRadius:6, border:`1px solid ${BORDER}`, background:CARD, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DARK, outline:"none", transition:"border 0.15s", boxShadow:"0 1px 3px rgba(44,36,22,0.06)" }}
            />
            {searchQ && (
              <button onClick={() => { setSearchQ(""); setSearchOpen(false); }}
                style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:14, color:DIM, lineHeight:1, padding:0 }}>×</button>
            )}
          </div>
          {searchOpen && searchQ.trim() && (
            <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:CARD, border:`1px solid ${BORDER}`, borderRadius:7, boxShadow:"0 8px 24px rgba(44,36,22,0.14)", zIndex:200, overflow:"hidden" }}>
              {!hasSearchResults ? (
                <div style={{ padding:"14px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM, fontStyle:"italic" }}>No results found</div>
              ) : (
                <>
                  {searchResults.jobs.length > 0 && (
                    <div>
                      <div style={{ padding:"8px 14px 4px", fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:DIM }}>Jobs</div>
                      {searchResults.jobs.map(j => (
                        <div key={j.id} onClick={() => { setSearchQ(""); setSearchOpen(false); onJobClick && onJobClick(j); }}
                          style={{ padding:"9px 14px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:`1px solid ${BORDER}`, transition:"background 0.1s" }}
                          onMouseOver={e => e.currentTarget.style.background = BG2}
                          onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                          <div>
                            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DARK, fontWeight:500 }}>{j.name} <IncompleteChip completeness={infoOf(j).c} margin={infoOf(j).m}/></div>
                            {j.clientName && <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, marginTop:1 }}>{j.clientName}</div>}
                          </div>
                          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color: j.profit >= 0 ? ACCENT2 : RED, fontWeight:600 }}>
                            {j.profit >= 0 ? "+" : ""}{$(j.profit)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.clients.length > 0 && (
                    <div>
                      <div style={{ padding:"8px 14px 4px", fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:DIM, borderTop: searchResults.jobs.length > 0 ? `1px solid ${BORDER}` : "none" }}>Clients</div>
                      {searchResults.clients.map(c => (
                        <div key={c} onClick={() => { setSearchQ(""); setSearchOpen(false); onClientClick && onClientClick(); }}
                          style={{ padding:"9px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, borderTop:`1px solid ${BORDER}`, transition:"background 0.1s" }}
                          onMouseOver={e => e.currentTarget.style.background = BG2}
                          onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                          <span style={{ fontSize:14, color:DIM }}>◉</span>
                          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DARK }}>{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Date range controls ── */}
        <div className="r-datebtns" style={{ display:"flex",alignItems:"center",gap:6,marginTop:4,flexWrap:"wrap",flexShrink:0 }}>
          <div className="r-seg" style={{ display:"flex",border:`1px solid ${BORDER}`,borderRadius:5,overflow:"hidden",background:CARD }}>
            {DATE_RANGES.map((r,i) => (
              <button key={r.key} onClick={()=>setDateRange(r.key)} style={{ cursor:"pointer",padding:"7px 13px",fontSize:11,fontWeight:500,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.03em",border:"none",borderRight:i<DATE_RANGES.length-1?`1px solid ${BORDER}`:"none",background:dateRange===r.key?ACCENT:CARD,color:dateRange===r.key?CARD:MID,transition:"all 0.15s" }}>{r.label}</button>
            ))}
          </div>
          <button onClick={()=>setDateRange("custom")} style={{ cursor:"pointer",padding:"7px 14px",fontSize:11,fontWeight:500,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.03em",border:`1px solid ${BORDER}`,borderRadius:5,background:dateRange==="custom"?ACCENT:CARD,color:dateRange==="custom"?CARD:MID,transition:"all 0.15s" }}>Custom</button>
          {dateRange === "custom" && (
            <div className="r-dates" style={{ display:"flex",alignItems:"center",gap:6,marginLeft:4 }}>
              <input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)}
                style={{ padding:"5px 10px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DARK,outline:"none",cursor:"pointer" }}
              />
              <span style={{ fontSize:11,color:DIM,fontFamily:"'DM Sans',sans-serif" }}>→</span>
              <input type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)}
                style={{ padding:"5px 10px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DARK,outline:"none",cursor:"pointer" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Connect QuickBooks banner — shown for QB clients who haven't connected yet */}
      {clientType === "quickbooks" && !qbConnected && (
        <div className="r-stack" style={{ marginBottom:24, padding:"16px 22px", borderRadius:6, border:`1px solid rgba(140,107,48,0.3)`, background:"rgba(140,107,48,0.05)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
          <div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:500, color:AMBER, marginBottom:3 }}>Connect your QuickBooks account</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:MID }}>{qbBannerMessage(dataSource)}</div>
          </div>
          <button
            type="button"
            onClick={onConnectQuickBooks}
            disabled={qbStarting}
            style={{ cursor:qbStarting?"default":"pointer", opacity:qbStarting?0.6:1, padding:"9px 20px", borderRadius:4, fontSize:12, fontWeight:500, border:`1px solid ${AMBER}`, color:CARD, background:AMBER, fontFamily:"'DM Sans',sans-serif", textDecoration:"none", whiteSpace:"nowrap", marginLeft:24, transition:"all 0.15s" }}
          >
            Connect QuickBooks →
          </button>
        </div>
      )}

      {/* ── Job Health Alerts card ── */}
      {!compact && (() => {
        const totalAlerts = unbilledJobs.length + atRiskJobs.length + lossJobs.length + lowMarginJobs.length + incompleteJobs.length;
        const accentColor = unbilledJobs.length > 0 ? "#C45C2A" : lossJobs.length > 0 ? RED : AMBER;
        const BURNT_ORANGE = "#C45C2A";
        const alertGroups = [
          { key:"unbilled",   label:"Unbilled",   color:BURNT_ORANGE, bg:"rgba(196,92,42,0.07)",   border:"rgba(196,92,42,0.30)",  filled:true,  jobs:unbilledJobs,   headline: j => `${j.length} job${j.length!==1?"s":""} have costs but no invoice sent`,   sub: j => `${$(j.reduce((s,x)=>s+x.costs,0))} potentially unbilled`,   names: j => j.slice(0,4).map(x=>x.name).join(" · ") + (j.length>4?` +${j.length-4} more`:"") },
          { key:"atrisk",     label:"At risk",    color:AMBER,        bg:"rgba(196,144,32,0.07)",  border:"rgba(196,144,32,0.28)", filled:true,  jobs:atRiskJobs,     headline: j => `${j.length} active job${j.length!==1?"s":""} trending toward a loss`,    sub: () => "costs above 85% of revenue so far",                          names: j => j.slice(0,4).map(x=>`${x.name} (${marginNote(x)})`).join(" · ") + (j.length>4?` +${j.length-4} more`:"") },
          { key:"loss",       label:"Loss",       color:RED,          bg:"rgba(156,53,53,0.06)",   border:"rgba(156,53,53,0.25)",  filled:false, jobs:lossJobs,       headline: j => `${j.length} completed job${j.length!==1?"s":""} closed at a loss`,        sub: j => `${$(Math.abs(j.reduce((s,x)=>s+x.profit,0)))} total lost`,   names: j => j.slice(0,4).map(x=>`${x.name} (${marginNote(x)})`).join(" · ") + (j.length>4?` +${j.length-4} more`:"") },
          { key:"lowmargin",  label:"Low margin", color:AMBER,        bg:"rgba(196,144,32,0.04)",  border:"rgba(196,144,32,0.18)", filled:false, jobs:lowMarginJobs,  headline: j => `${j.length} job${j.length!==1?"s":""} finished under 10% margin`,       sub: () => null,                                                         names: j => j.slice(0,4).map(x=>`${x.name} (${marginNote(x)})`).join(" · ") + (j.length>4?` +${j.length-4} more`:"") },
          { key:"incomplete", label:"Incomplete",  color:MID,          bg:"rgba(107,94,78,0.05)",   border:"rgba(107,94,78,0.25)",  filled:false, jobs:incompleteJobs, headline: j => `${j.length} job${j.length!==1?"s":""} still missing numbers`,            sub: () => "their margins may look better than they are",              names: j => j.slice(0,4).map(x=>`${x.name} (${infoOf(x).c.reasons.join(", ")})`).join(" · ") + (j.length>4?` +${j.length-4} more`:"") },
        ].filter(g => g.jobs.length > 0);

        return (
          <div style={{ marginBottom:20, borderRadius:6, border:`1px solid ${BORDER}`, borderLeft: totalAlerts > 0 ? `4px solid ${accentColor}` : `4px solid ${ACCENT2}`, background:CARD, overflow:"hidden", boxShadow:"0 1px 4px rgba(44,36,22,0.04)" }}>
            {/* Collapsed header — always visible */}
            <div onClick={() => setAlertsOpen(o => !o)}
              className="r-wrap" style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 18px", cursor:"pointer", userSelect:"none" }}>
              {/* Title */}
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, color: totalAlerts > 0 ? MID : ACCENT2 }}>
                {totalAlerts > 0 ? `${totalAlerts} alert${totalAlerts!==1?"s":""}` : "No alerts"}
              </div>
              {/* Chips */}
              {totalAlerts > 0 && (
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {alertGroups.map(g => (
                    <span key={g.key} style={{ fontSize:10, fontWeight:600, fontFamily:"'DM Sans',sans-serif", padding:"2px 8px", borderRadius:4, color: g.filled ? CARD : g.color, background: g.filled ? g.color : `${g.color}12`, border:`1px solid ${g.color}40` }}>
                      {g.jobs.length} {g.label.toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
              {totalAlerts === 0 && (
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:DIM }}>All jobs looking healthy</span>
              )}
              {/* Chevron */}
              <span style={{ marginLeft:"auto", fontSize:10, color:DIM }}>{alertsOpen ? "▲" : "▼"}</span>
            </div>

            {/* Expanded detail */}
            {alertsOpen && totalAlerts > 0 && (
              <div style={{ borderTop:`1px solid ${BORDER}` }}>
                {alertGroups.map((g, gi) => (
                  <div key={g.key} style={{ padding:"12px 18px", borderBottom: gi < alertGroups.length-1 ? `1px solid ${BORDER}` : "none", background: g.bg }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5, flexWrap:"wrap" }}>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:"'DM Sans',sans-serif", letterSpacing:"0.05em", padding:"1px 7px", borderRadius:3, color: g.filled ? CARD : g.color, background: g.filled ? g.color : "transparent", border:`1px solid ${g.color}` }}>
                        {g.label}
                      </span>
                      <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:500, color: g.filled ? g.color : MID }}>{g.headline(g.jobs)}</span>
                      {g.sub(g.jobs) && <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:MID }}>· {g.sub(g.jobs)}</span>}
                    </div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:MID, paddingLeft:4 }}>{g.names(g.jobs)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* KPI Drilldown Modal */}
      {activeKpi && (
        <KpiModal
          type={activeKpi}
          expenseView={expenseView}
          jobSummaries={typeFilteredJobs}
          allJobSummaries={jobSummaries}
          overhead={overhead}
          untagged={filteredUntagged}
          pendingItems={untagged}
          totalRev={totalRev}
          totalCost={totalCost}
          totalOverhead={totalOverhead}
          totalProfit={totalProfit}
          accountedFor={accountedFor}
          totalExpenses={totalExpenses}
          onClose={() => setActiveKpi(null)}
          onJobClick={(job) => { setActiveKpi(null); onJobClick(job); }}
          onJumpToInbox={onJumpToInbox}
        />
      )}

      {/* ── KPI Hero Cards ── */}
      {!compact && <div className="r-cols-kpi" style={{ display:"grid", gridTemplateColumns: "1fr 1fr 1fr", gap: isMobile ? 12 : 18, marginBottom:28 }}>

        {/* PROFIT */}
        <div className="pls card" onClick={()=>setActiveKpi('profit')}
          style={{ padding:"22px 28px", cursor:"pointer", borderTop:`4px solid ${heroColor}`, display:"flex", flexDirection:"column", justifyContent:"space-between", minHeight:130 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:DIM, textTransform:"uppercase" }}>
              {heroIsNet ? "Net Profit" : "Gross Profit"}
            </div>
            <div style={{ display:"flex", border:`1px solid ${BORDER}`, borderRadius:3, overflow:"hidden" }} onClick={e=>e.stopPropagation()}>
              {[["job","Gross"],["fixed","Net"]].map(([k,l],i) => (
                <button key={k} onClick={e=>{e.stopPropagation();setExpenseView(k);}}
                  style={{ cursor:"pointer", padding:"3px 10px", fontSize:10, fontWeight:600, fontFamily:"'DM Sans',sans-serif", border:"none", borderRight:i===0?`1px solid ${BORDER}`:"none", background:expenseView===k?ACCENT2:CARD, color:expenseView===k?CARD:DIM, transition:"all 0.15s" }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ fontFamily:"'Lora',serif", fontSize:46, fontWeight:700, color:heroColor, letterSpacing:"-0.03em", lineHeight:1.05, margin:"8px 0 6px" }}>{$(heroProfit)}</div>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            {ppcProfitPct != null && (
              <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:500, color: ppcProfitPct >= 0 ? ACCENT2 : RED, display:"flex", alignItems:"center", gap:3 }}>
                <span style={{ fontSize:9 }}>{ppcProfitPct >= 0 ? "▲" : "▼"}</span> {Math.abs(ppcProfitPct)}%
                <span style={{ fontWeight:400, color:DIM, fontSize:10 }}>&nbsp;{ppcLbl}</span>
              </span>
            )}
            <div className="kpi-tooltip" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:DIM, display:"inline-flex" }}>
              {heroMargin === null ? "No margin yet. No revenue recorded." : `${heroMargin}% ${heroIsNet ? "net" : "gross"} margin${marginMissingCount > 0 ? " so far" : ""}`}
              <span className="tooltip-text" style={{ width:200 }}>
                {heroIsNet ? "Net margin" : "Gross margin"} = Profit ÷ Revenue. {heroIsNet ? "Accounts for fixed costs." : "Job costs only."}{marginMissingCount > 0 ? ` ${marginMissingCount} job${marginMissingCount!==1?"s are":" is"} missing ${businessType.missingLaborCounts ? "costs or labor" : "costs"}, so this may look better than it is.` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* REVENUE */}
        <div className="pls card" onClick={()=>setActiveKpi('revenue')}
          style={{ padding:"22px 24px", cursor:"pointer", borderTop:`4px solid ${ACCENT}`, display:"flex", flexDirection:"column", justifyContent:"space-between", minHeight:130 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:DIM, textTransform:"uppercase" }}>Revenue</div>
          <div>
            <div style={{ fontFamily:"'Lora',serif", fontSize:38, fontWeight:700, color:DARK, margin:"8px 0 4px" }}>{$(totalRev)}</div>
            {ppcRevPct != null ? (
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:500, color: ppcRevPct >= 0 ? ACCENT2 : RED, display:"flex", alignItems:"center", gap:3, marginBottom:2 }}>
                <span style={{ fontSize:9 }}>{ppcRevPct >= 0 ? "▲" : "▼"}</span> {Math.abs(ppcRevPct)}%
                <span style={{ fontWeight:400, color:DIM, fontSize:10 }}>&nbsp;{ppcLbl}</span>
              </div>
            ) : null}
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM }}>{typeFilteredJobs.length} job{typeFilteredJobs.length!==1?"s":""} billed</div>
          </div>
        </div>

        {/* EXPENSES */}
        <div className="pls card" onClick={()=>setActiveKpi('expenses')}
          style={{ padding:"22px 24px", cursor:"pointer", borderTop:`4px solid ${AMBER}`, display:"flex", flexDirection:"column", justifyContent:"space-between", minHeight:130 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:DIM, textTransform:"uppercase" }}>
              {heroIsNet ? "Total Expenses" : "Job Expenses"}
            </div>
            {/* DQ badge with tooltip */}
            {(() => {
              const untaggedActive = filteredUntagged.filter(u => u.status !== 'dismissed');
              const hasIssues = untaggedActive.length > 0;
              return (
                <div className="kpi-tooltip" onClick={e => { e.stopPropagation(); setActiveKpi('quality'); }}
                  style={{ border:`1.5px solid ${dqColor}`, borderRadius:5, background:`${dqColor}18`, padding:"4px 9px", cursor:"pointer", textAlign:"center", flexShrink:0, transition:"opacity 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.opacity="0.7"}
                  onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, color:dqColor, letterSpacing:"0.04em" }}>
                    {hasIssues ? `${untaggedActive.length} untagged` : "All tagged"}
                  </div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:9, color:dqColor, marginTop:2 }}>
                    {dataQuality}% quality
                  </div>
                  <span className="tooltip-text" style={{ width:200 }}>
                    Data Quality Score: {dataQuality}% of expenses are tagged to a job or marked as overhead. A higher score means more accurate profit numbers.
                  </span>
                </div>
              );
            })()}
          </div>
          <div>
            <div style={{ fontFamily:"'Lora',serif", fontSize:38, fontWeight:700, color:MID, margin:"8px 0 4px" }}>
              {heroIsNet ? $(totalCost + totalOverhead) : $(totalCost)}
            </div>
            {ppcCostPct != null ? (
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:500, color: ppcCostPct <= 0 ? ACCENT2 : RED, display:"flex", alignItems:"center", gap:3, marginBottom:2 }}>
                <span style={{ fontSize:9 }}>{ppcCostPct <= 0 ? "▼" : "▲"}</span> {Math.abs(ppcCostPct)}%
                <span style={{ fontWeight:400, color:DIM, fontSize:10 }}>&nbsp;{ppcLbl}</span>
              </div>
            ) : null}
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM }}>
              {heroIsNet
                ? `${$(totalCost)} job + ${$(totalOverhead)} fixed`
                : costSummaryLine(businessType, totalMaterial, totalLabor, $)}
            </div>
          </div>
        </div>

      </div>}

      {/* ── Revenue Goals Panel ── */}
      <RevenueGoals goals={revenueGoals} jobSummaries={jobSummaries} onAdd={onAddGoal} onEdit={onEditGoal} onDelete={onDeleteGoal} onMarkComplete={onMarkCompleteGoal} onArchive={onArchiveGoal}/>

      {/* ── Active Jobs table — full width ── */}
      <div ref={jobsRef} className="card r-card-pad" style={{ padding:"22px 26px", marginBottom:28 }}>
        <div className="r-wrap" style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, gap:10 }}>
          <div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:compact?16:10,letterSpacing:compact?0:"0.1em",color:compact?DARK:DIM,textTransform:compact?"none":"uppercase",marginBottom:5,fontWeight:compact?600:500 }}>{compact ? "Your jobs" : "Active Jobs"}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>{sorted.length} job{sorted.length!==1?"s":""} · {compact ? "choose how to sort" : "click headers to sort"}</div>
            {compact && jobFilter && (
              <button type="button" className="btn" onClick={() => setJobFilter(null)} style={{ marginTop:8, fontSize:14, color:ACCENT }}>
                Showing {FILTER_NAMES[jobFilter]}. Show all jobs
              </button>
            )}
          </div>
          {sorted.length >= 2 && onCompare && (
            <button className="btn" onClick={onCompare} style={{ fontSize:11, padding:"6px 14px" }}>Compare Jobs</button>
          )}
        </div>
        {sorted.length === 0 ? (
          <div style={{ padding:"36px 0",textAlign:"center" }}>
            <div style={{ fontFamily:"'Lora',serif",fontSize:14,color:MID,fontStyle:"italic",marginBottom:6 }}>{compact && jobFilter ? emptyFilterLine(jobFilter) : "No jobs in this period"}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM }}>Try selecting a different date range above.</div>
          </div>
        ) : (
          <>
            {compact ? (
              <div>
                {/* Phone: the same jobs as stacked cards. Sort comes from this control instead of the column headings. */}
                <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                  <select aria-label="Sort jobs by" className="job-select" value={sort} onChange={e => { setSort(e.target.value); setSortDir("desc"); }} style={{ flex:1 }}>
                    {JOB_SORT_CHOICES.map(c => <option key={c.key} value={c.key}>Sort by {c.label.toLowerCase()}</option>)}
                  </select>
                  <button className="btn" aria-label={sortDir === "asc" ? "Sorted low to high. Tap to reverse." : "Sorted high to low. Tap to reverse."} onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")} style={{ minWidth:48 }}>{sortDir === "asc" ? "↑" : "↓"}</button>
                </div>
                {sorted.slice(0, jobTableRows).map((j, i) => {
                  const { c, m: mv } = infoOf(j);
                  const m = mv.kind === "number" ? mv.value : null;
                  const profitColor = j.profit < 0 ? RED : m === null ? DIM : m >= 20 ? ACCENT2 : m >= 10 ? AMBER : DIM;
                  const barColor = m === null ? AMBER : m >= 20 ? ACCENT2 : m >= 10 ? AMBER : RED;
                  return (
                    <div key={j.id || i} role={onJobClick ? "button" : undefined} tabIndex={onJobClick ? 0 : undefined}
                      onClick={() => onJobClick && onJobClick(j)}
                      onKeyDown={e => { if (onJobClick && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onJobClick(j); } }}
                      style={{ padding:"14px 0", borderTop:`1px solid ${BORDER}`, cursor: onJobClick ? "pointer" : "default" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:15, fontWeight:600, color:DARK, overflowWrap:"anywhere" }}>{j.name}</div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:MID, marginTop:2, overflowWrap:"anywhere" }}>{j.clientName || "—"}</div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:15, fontWeight:700, color:profitColor }}>{j.profit >= 0 ? "+" : ""}{$(j.profit)}</div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM, marginTop:2 }}>Profit</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginTop:10 }}>
                        <div>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM }}>Revenue</div>
                          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:14, color:MID, marginTop:2 }}>{$(j.revenue)}</div>
                        </div>
                        <div style={{ textAlign:"center" }}>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM }}>Costs</div>
                          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:14, color:MID, marginTop:2 }}>{$(j.costs)}</div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM }}>Margin</div>
                          <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:2, flexWrap:"wrap", justifyContent:"flex-end" }}>
                            {mv.kind !== "incomplete" && <IncompleteChip completeness={c} margin={mv}/>}
                            <span title={mv.kind === "incomplete" ? c.reasons.join(". ") : undefined} style={{ fontFamily:"'DM Mono',monospace", fontSize:14, fontWeight:600, color:mv.kind === "number" ? barColor : mv.kind === "incomplete" ? AMBER : DIM }}>{mv.text}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="mob-scroll">
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:"'DM Sans',sans-serif", minWidth: 520 }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${BORDER}` }}>
                  {[
                    { col:"name",     label:"Job",       align:"left"  },
                    { col:"client",   label:"Client",    align:"left"  },
                    { col:"revenue",  label:"Revenue",   align:"right" },
                    ...businessType.columns.map(cc => ({ col: cc.id === "labor" ? "labor" : "material", label: cc.short, align:"right", tip: cc.tip })),
                    { col:"costs",    label:businessType.totalLabel, align:"right" },
                    { col:"profit",   label:"Profit",    align:"right" },
                    { col:"margin",   label:"Margin",    align:"right", tip:`Gross margin = Profit ÷ Revenue. Includes all costs. Shows Incomplete when costs${businessType.missingLaborCounts ? ` or ${businessType.laborWords.noun}` : ""} are missing, and a dash when there is no revenue.` },
                  ].map(({ col, label, align, tip }) => (
                    <th key={col} onClick={() => handleColSort(col)}
                      style={{ padding:"7px 10px", textAlign:align, fontSize:9, letterSpacing:"0.08em", textTransform:"uppercase", color: sort===col ? DARK : DIM, fontWeight:600, cursor:"pointer", userSelect:"none", whiteSpace:"nowrap", position:"relative" }}>
                      <span className={tip ? "kpi-tooltip" : undefined} style={{ display:"inline-flex", alignItems:"center", gap:3 }}>
                        {label}
                        {sort===col
                          ? <span style={{ marginLeft:4, color:ACCENT }}>{sortDir==="asc"?"↑":"↓"}</span>
                          : <span style={{ marginLeft:4, opacity:0.25 }}>↕</span>
                        }
                        {tip && <span className="tooltip-text" style={{ width:220, left:"auto", right:0, transform:"none", textTransform:"none", letterSpacing:"normal", fontWeight:400 }}>{tip}</span>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const numOf = j => { const mv = infoOf(j).m; return mv.kind === "number" ? mv.value : null; };
                  const maxM = Math.max(...sorted.map(j => Math.max(0, numOf(j) ?? 0)), 1);
                  return sorted.slice(0, jobTableRows).map((j, i) => {
                    const { c, m: mv } = infoOf(j);
                    const m = numOf(j); // null when there is no real margin to show ("—" or "Incomplete")
                    const profitColor = j.profit < 0 ? RED : m === null ? DIM : m >= 20 ? ACCENT2 : m >= 10 ? AMBER : DIM;
                    const barW = j.profit < 0 || m === null ? 0 : Math.min(100, (m / maxM) * 100);
                    const barColor = m === null ? AMBER : m >= 20 ? ACCENT2 : m >= 10 ? AMBER : RED;
                    return (
                      <tr key={j.id || i}
                        onClick={() => onJobClick && onJobClick(j)}
                        style={{ borderBottom:`1px solid ${BORDER}`, cursor: onJobClick ? "pointer" : "default", transition:"background 0.1s" }}
                        onMouseOver={e => e.currentTarget.style.background = BG2}
                        onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding:"9px 10px", color:DARK, fontWeight:500, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={j.name}>{j.name}</td>
                        <td style={{ padding:"9px 10px", color:MID, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={j.clientName}>{j.clientName || "—"}</td>
                        <td style={{ padding:"9px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, color:MID }}>{$(j.revenue)}</td>
                        {businessType.columns.map(cc => cc.id === "labor"
                          ? <td key={cc.id} style={{ padding:"9px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, color:DIM }}>{c.flags.noLabor && businessType.missingLaborCounts ? <span title={businessType.laborWords.noneRecorded}>—</span> : $(j.laborCost || 0)}</td>
                          : <td key={cc.id} style={{ padding:"9px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, color:DIM }}>{$(j.materialCost || 0)}</td>)}
                        <td style={{ padding:"9px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, color:MID }}>{$(j.costs)}</td>
                        <td style={{ padding:"9px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, fontWeight:700, color:profitColor }}>{j.profit >= 0 ? "+" : ""}{$(j.profit)}</td>
                        <td style={{ padding:"9px 10px", textAlign:"right" }}>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                            <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                              {mv.kind !== "incomplete" && <IncompleteChip completeness={c} margin={mv}/>}
                              <span title={mv.kind === "incomplete" ? c.reasons.join(". ") : undefined} style={{ fontFamily:"'DM Mono',monospace", fontSize:11, fontWeight:600, color:mv.kind === "number" ? barColor : mv.kind === "incomplete" ? AMBER : DIM }}>{mv.text}</span>
                            </span>
                            {m !== null && (
                              <div style={{ width:52, height:3, background:BORDER, borderRadius:2, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${barW}%`, background:barColor, borderRadius:2, transition:"width 0.3s" }}/>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            </div>
            )}{/* end mob-scroll */}
            {sorted.length > 10 && (
              <div style={{ marginTop:12, textAlign:"center" }}>
                {jobTableRows < sorted.length ? (
                  <button className="btn" onClick={() => setJobTableRows(sorted.length)} style={{ fontSize:11, color:ACCENT }}>
                    Show all {sorted.length} jobs ↓
                  </button>
                ) : (
                  <button className="btn" onClick={() => setJobTableRows(10)} style={{ fontSize:11, color:DIM }}>
                    Show less ↑
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}

export { Dashboard };
